'use client'

import { useEffect, useState } from 'react'

import { QR_LOGO_TRANSPARENT, type QrDesign, type QrLogoShape } from '@/lib/qr/design'
import { getQrLogoIcon, qrIconColors, svgDataUrl } from '@/lib/qr/logoIcons'

/**
 * Turns a design's logo into one square PNG data URL — tile shape, backdrop and the mark,
 * already composited — which the QR library then places in the cleared centre.
 *
 * Compositing up front, instead of handing the library a remote URL, is what makes the logo
 * survive every export: `toDataURL`/`toSVGString` render the SVG as an image, and an SVG
 * rendered as an image never loads external resources. A data URL is part of the markup.
 */

export type QrLogoArt = {
  src: string
  /** `cover` crops a photo into the tile; `contain` keeps a logo whole inside it. */
  fit: 'cover' | 'contain'
  /** Padding inside the tile, as a fraction of its edge. */
  inset: number
  /** Tile fill forced by the art (a filled brand icon); otherwise the design's backdrop wins. */
  tile?: string | null
}

type Sources = {
  companyLogo?: string | null
  cardPhoto?: string | null
}

/** Which image the design's logo source points at, and how it sits in its tile. */
export const resolveQrLogoArt = (design: QrDesign, sources: Sources = {}): QrLogoArt | null => {
  switch (design.logoSource) {
    case 'company':
      return sources.companyLogo ? { src: sources.companyLogo, fit: 'contain', inset: 0.08 } : null
    case 'card':
      return sources.cardPhoto ? { src: sources.cardPhoto, fit: 'cover', inset: 0 } : null
    case 'upload':
      return design.logoImage ? { src: design.logoImage, fit: 'contain', inset: 0.06 } : null
    case 'icon': {
      const icon = getQrLogoIcon(design.logoIcon)
      if (!icon) return null
      const { ink, tile } = qrIconColors(icon, design.logoIconStyle, design.foregroundColor)
      return { src: svgDataUrl(icon.svg(ink)), fit: 'contain', inset: 0.15, tile }
    }
    default:
      return null
  }
}

/** Canvas edge of the composite. The code never shows the tile larger than ~300px on screen; exports scale it. */
const CANVAS = 256

const tilePath = (ctx: CanvasRenderingContext2D, edge: number, shape: QrLogoShape) => {
  ctx.beginPath()
  if (shape === 'circle') {
    ctx.arc(edge / 2, edge / 2, edge / 2, 0, Math.PI * 2)
  } else {
    const r = shape === 'rounded' ? edge * 0.24 : 0
    ctx.moveTo(r, 0)
    ctx.arcTo(edge, 0, edge, edge, r)
    ctx.arcTo(edge, edge, 0, edge, r)
    ctx.arcTo(0, edge, 0, 0, r)
    ctx.arcTo(0, 0, edge, 0, r)
  }
  ctx.closePath()
}

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new window.Image()
    // Remote logos need CORS for the canvas to be readable; data URLs ignore it.
    if (!src.startsWith('data:')) image.crossOrigin = 'anonymous'
    image.onload = () => resolve(image)
    image.onerror = reject
    image.src = src
  })

const compose = async (art: QrLogoArt, shape: QrLogoShape, background: string) => {
  const image = await loadImage(art.src)
  const canvas = document.createElement('canvas')
  canvas.width = CANVAS
  canvas.height = CANVAS
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  tilePath(ctx, CANVAS, shape)
  const fill = art.tile || background
  if (fill && fill !== QR_LOGO_TRANSPARENT) {
    ctx.fillStyle = fill
    ctx.fill()
  }
  ctx.clip()

  const pad = CANVAS * art.inset
  const box = CANVAS - pad * 2
  // SVGs can report no intrinsic size — treat them as square.
  const iw = image.naturalWidth || box
  const ih = image.naturalHeight || box
  const scale = art.fit === 'cover' ? Math.max(box / iw, box / ih) : Math.min(box / iw, box / ih)
  const w = iw * scale
  const h = ih * scale
  ctx.drawImage(image, pad + (box - w) / 2, pad + (box - h) / 2, w, h)

  // Throws on a tainted canvas (remote logo served without CORS) — handled by the caller.
  return canvas.toDataURL('image/png')
}

/** Composites are pure functions of their inputs; share them between the panel, preview and exports. */
const cache = new Map<string, Promise<string | null>>()
const settled = new Map<string, string | null>()
const CACHE_LIMIT = 48

const composeCached = (key: string, art: QrLogoArt, shape: QrLogoShape, background: string) => {
  let job = cache.get(key)
  if (!job) {
    job = compose(art, shape, background)
      // A logo that cannot be composited still shows on screen as the raw image; only
      // exports lose it, which is the best a non-CORS host allows.
      .catch(() => (art.src.startsWith('data:') ? null : art.src))
      .then((url) => {
        settled.set(key, url)
        return url
      })
    cache.set(key, job)
    if (cache.size > CACHE_LIMIT) {
      const oldest = cache.keys().next().value as string
      cache.delete(oldest)
      settled.delete(oldest)
    }
  }
  return job
}

/**
 * The composited logo for a design, or `null` when it has none. While a new composite is
 * drawing, the previous one stays up so dragging through options never flashes an empty centre.
 */
export const useQrLogo = (
  art: QrLogoArt | null,
  design: Pick<QrDesign, 'logoShape' | 'logoBackground'>
) => {
  const key = art
    ? JSON.stringify([
        art.src,
        art.fit,
        art.inset,
        art.tile,
        design.logoShape,
        design.logoBackground,
      ])
    : null
  const [state, setState] = useState<{ key: string | null; url: string | null }>(() => ({
    key,
    url: key ? (settled.get(key) ?? null) : null,
  }))

  useEffect(() => {
    if (!key || !art) return
    if (settled.has(key)) {
      setState({ key, url: settled.get(key) ?? null })
      return
    }
    let cancelled = false
    composeCached(key, art, design.logoShape, design.logoBackground).then((url) => {
      if (!cancelled) setState({ key, url })
    })
    return () => {
      cancelled = true
    }
    // `key` captures every input of `art` and the design fields used.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  if (!key) return null
  return state.key === key ? state.url : (settled.get(key) ?? state.url)
}

/** Largest edge an uploaded logo is kept at — plenty for a tile that is a fraction of the code. */
const UPLOAD_EDGE = 320
export const QR_UPLOAD_MAX_BYTES = 5 * 1024 * 1024

/**
 * Reads an uploaded image and downsizes it to a compact data URL, so the saved design stays
 * small and the logo never depends on a remote host. SVGs are rasterised on the way in.
 */
export const readQrLogoUpload = async (file: File): Promise<string> => {
  if (!file.type.startsWith('image/')) throw new Error('Choose an image file.')
  if (file.size > QR_UPLOAD_MAX_BYTES) throw new Error('Choose an image under 5 MB.')

  const url = URL.createObjectURL(file)
  try {
    const image = await loadImage(url)
    const iw = image.naturalWidth || UPLOAD_EDGE
    const ih = image.naturalHeight || UPLOAD_EDGE
    const scale = Math.min(1, UPLOAD_EDGE / Math.max(iw, ih))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(iw * scale))
    canvas.height = Math.max(1, Math.round(ih * scale))
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('This browser cannot read images.')
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
    // WebP keeps transparency at a fraction of PNG's size; browsers without it return PNG.
    return canvas.toDataURL('image/webp', 0.92)
  } catch (error) {
    throw error instanceof Error && error.message
      ? error
      : new Error('That image could not be read.')
  } finally {
    URL.revokeObjectURL(url)
  }
}
